import { motion } from 'framer-motion';
import { Trophy, Clock, Users, Brain, Crown, Target, Code2 } from 'lucide-react';
import { useState } from 'react';
import GeeksLeagueRegistration, { RegistrationData } from './GeeksLeagueRegistration';

// Import leader images
import khaireddinePic from '../assets/geeks/Khaireddine.jpeg';
import abdoPic from '../assets/geeks/abdo.jpeg';
import mohaPic from '../assets/geeks/Moha.jpeg';

interface Participant {
  name: string;
  points: number;
  time: string;
  isLeader?: boolean;
}

interface Group {
  name: string;
  leader: {
    name: string;
    image: string;
    title: string;
  };
  participants: Participant[];
}

export default function GeeksLeague() {
  const [groups] = useState<Group[]>([
    {
      name: 'Group A',
      leader: {
        name: 'Khaireddine Dalaa',
        image: khaireddinePic,
        title: 'Team Alpha Leader'
      },
      participants: [
        { name: 'Khaireddine Dalaa', points: 0, time: '00:00:00', isLeader: true },
        { name: 'Challenger #2', points: 0, time: '00:00:00' },
        { name: 'Challenger #3', points: 0, time: '00:00:00' },
        { name: 'Challenger #4', points: 0, time: '00:00:00' },
        { name: 'Challenger #5', points: 0, time: '00:00:00' }
      ]
    },
    {
      name: 'Group B',
      leader: {
        name: 'AbdelAziz Seqqal',
        image: abdoPic,
        title: 'Team Beta Leader'
      },
      participants: [
        { name: 'AbdelAziz Seqqal', points: 0, time: '00:00:00', isLeader: true },
        { name: 'Challenger #2', points: 0, time: '00:00:00' },
        { name: 'Challenger #3', points: 0, time: '00:00:00' },
        { name: 'Challenger #4', points: 0, time: '00:00:00' },
        { name: 'Challenger #5', points: 0, time: '00:00:00' }
      ]
    },
    {
      name: 'Group C',
      leader: {
        name: 'Mohammed Kadi',
        image: mohaPic,
        title: 'Team Gamma Leader'
      },
      participants: [
        { name: 'Mohammed Kadi', points: 0, time: '00:00:00', isLeader: true },
        { name: 'Challenger #2', points: 0, time: '00:00:00' },
        { name: 'Challenger #3', points: 0, time: '00:00:00' },
        { name: 'Challenger #4', points: 0, time: '00:00:00' },
        { name: 'Challenger #5', points: 0, time: '00:00:00' }
      ]
    }
  ]);

  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);

  const handleRegistrationSubmit = (data: RegistrationData) => {
    // Here you would typically send this data to your backend
    console.log('Registration submitted:', data);
    // You can add API call here to save the registration
    // For now, we'll just close the modal
    setIsRegistrationOpen(false);
  };

  return (
    <section className="relative py-24 bg-gradient-to-b from-[#190B4D] to-black overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
      </div>

      <div className="relative container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* League Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block">
              <h2 className="text-6xl md:text-7xl font-bold text-white mb-6 relative">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#512BD4] to-[#7B61FF]">
                  GeeksLeague
                </span>
                <div className="absolute -top-6 -right-6 text-yellow-500 animate-bounce">
                  <Crown className="w-8 h-8" />
                </div>
              </h2>
              <p className="text-xl text-gray-400 italic">Where Code Meets Competition</p>
            </div>
          </motion.div>

          {/* League Format */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xl text-gray-300 mb-8">
              Join the ultimate battle arena where developers compete in live problem-solving challenges.
              Three legendary teams, two hours of intense coding, one champion team.
            </p>
          </motion.div>

          {/* Groups Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {groups.map((group, groupIndex) => (
              <motion.div
                key={group.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: groupIndex * 0.2 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6"
              >
                <div className="text-2xl font-bold text-white mb-6 text-center">{group.name}</div>
                
                {/* Leader Section */}
                <div className="flex flex-col items-center mb-8">
                  <div className="w-32 h-32 rounded-full overflow-hidden mb-4 bg-gradient-to-r from-[#512BD4] to-[#7B61FF] p-1">
                    <div className="w-full h-full rounded-full overflow-hidden bg-cover bg-center relative">
                      <img
                        src={group.leader.image}
                        alt={group.leader.name}
                        className={`w-full h-full object-cover absolute ${
                          group.leader.name === 'Khaireddine Dalaa'
                            ? 'object-[90%_-20%] scale-150'
                            : group.leader.name === 'AbdelAziz Seqqal'
                            ? 'object-[50%_20%] scale-150'
                            : 'object-[50%_-15%] scale-125'
                        }`}
                      />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{group.leader.name}</h3>
                  <p className="text-gray-400">{group.leader.title}</p>
                </div>

                {/* Participants List */}
                <div className="space-y-3">
                  {group.participants.map((participant, index) => (
                    <motion.div
                      key={`${group.name}-${index}`}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        participant.isLeader ? 'bg-[#512BD4]/20' : 'bg-white/5'
                      }`}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-400">{index + 1}.</span>
                        <span className="text-white">{participant.name}</span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="text-gray-400">{participant.points} Pts</span>
                        <span className="text-gray-400">{participant.time}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <motion.div
              className="text-center p-6 bg-white/5 rounded-xl backdrop-blur-sm"
              whileHover={{ y: -10 }}
            >
              <Clock className="w-12 h-12 text-[#512BD4] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">2-Hour Challenges</h3>
              <p className="text-gray-400">Live coding sessions with real-time problem solving</p>
            </motion.div>

            <motion.div
              className="text-center p-6 bg-white/5 rounded-xl backdrop-blur-sm"
              whileHover={{ y: -10 }}
            >
              <Brain className="w-12 h-12 text-[#512BD4] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Expert Leaders</h3>
              <p className="text-gray-400">Learn from experienced developers and mentors</p>
            </motion.div>

            <motion.div
              className="text-center p-6 bg-white/5 rounded-xl backdrop-blur-sm"
              whileHover={{ y: -10 }}
            >
              <Trophy className="w-12 h-12 text-[#512BD4] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Weekly Prizes</h3>
              <p className="text-gray-400">Compete for recognition and rewards</p>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <button 
              onClick={() => setIsRegistrationOpen(true)}
              className="px-8 py-4 bg-gradient-to-r from-[#512BD4] to-[#7B61FF] text-white rounded-lg font-bold hover:from-[#7B61FF] hover:to-[#512BD4] transition-all duration-300 transform hover:-translate-y-1"
            >
              Join the League
            </button>
            <p className="mt-4 text-gray-400">Next session starts soon. Reserve your spot!</p>
          </motion.div>
        </div>
      </div>

      {/* Registration Modal */}
      <GeeksLeagueRegistration
        isOpen={isRegistrationOpen}
        onClose={() => setIsRegistrationOpen(false)}
        onSubmit={handleRegistrationSubmit}
      />
    </section>
  );
} 